import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  MessageCircle, 
  Copy, 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertTriangle,
  ArrowRight,
  RefreshCw,
  Wifi,
  WifiOff,
  CreditCard,
  Send
} from 'lucide-react';

// WebSocket URL
const WS_BASE_URL = 'wss://api.kit8exchange.com';

// Support Telegram
const SUPPORT_TELEGRAM = 'https://t.me/kit8exchange_support';

const OrderConfirmationScreen = ({ 
  orderDetails, 
  usdtToCnyRate,
  exchangeRate,
  tg, 
  setCurrentScreen, 
  setOrderDetails,
  apiUrl,
  makeAuthenticatedRequest 
}) => {
  const [liveOrderData, setLiveOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Open support chat in Telegram
  const openSupportChat = () => {
    window.open(SUPPORT_TELEGRAM, '_blank');
  };

  // Fetch order data from server
  const fetchOrderData = useCallback(async () => {
    const actualOrderId = orderDetails?.orderId || orderDetails?.order_id || orderDetails?.id;
    
    if (!actualOrderId || !makeAuthenticatedRequest || !apiUrl) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`${apiUrl}/api/order/${actualOrderId}`);
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Сервер вернул некорректный ответ');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Ошибка сервера: ${response.status}`);
      }

      const data = await response.json();
      setLiveOrderData(data);
      setError(null);
      setIsLoading(false);
      
      // Check if order failed
      if (data.status === 'failed') {
        setIsFailed(true);
      }
    } catch (err) {
      console.error('Order fetch error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [orderDetails, apiUrl, makeAuthenticatedRequest]);

  // Initial load
  useEffect(() => {
    fetchOrderData();
  }, [fetchOrderData]);

  // WebSocket connection
  useEffect(() => {
    const actualOrderId = orderDetails?.orderId || orderDetails?.order_id || orderDetails?.id;
    
    if (!actualOrderId) return;
    
    const currentStatus = liveOrderData?.status || orderDetails?.status;
    if (['completed', 'cancelled', 'failed'].includes(currentStatus)) {
      return;
    }

    const connectWebSocket = () => {
      try {
        const wsUrl = `${WS_BASE_URL}/ws/order/${actualOrderId}`;
        console.log('WebSocket connecting:', wsUrl);
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('WebSocket connected');
          setWsConnected(true);
          
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send('ping');
            }
          }, 30000);
        };

        ws.onmessage = (event) => {
          try {
            if (event.data === 'pong') return;

            const message = JSON.parse(event.data);
            console.log('WebSocket message:', message);

            if (message.type === 'connected' && message.status) {
              setLiveOrderData(prev => prev ? { ...prev, status: message.status } : prev);
            }

            if (message.type === 'status_update') {
              setLiveOrderData(prev => prev ? { ...prev, status: message.status } : prev);
              
              if (message.status === 'failed') {
                setIsFailed(true);
              }

              if (['completed', 'cancelled', 'failed'].includes(message.status)) {
                ws.close();
              }
            }
          } catch (e) {
            console.error('WebSocket parse error:', e);
          }
        };

        ws.onerror = () => {
          setWsConnected(false);
        };

        ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code);
          setWsConnected(false);
          
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }

          const currentStatus = liveOrderData?.status;
          if (!['completed', 'cancelled', 'failed'].includes(currentStatus) && event.code !== 1000) {
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
          }
        };

      } catch (err) {
        console.error('WebSocket error:', err);
        setWsConnected(false);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmount');
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [orderDetails, liveOrderData?.status]);

  const displayData = liveOrderData || orderDetails;

  // Copy to clipboard
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(label);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // Parse payment details
  const parsePaymentDetails = () => {
    const rawDetails = displayData?.paymentDetails || displayData?.payment_details;
    if (!rawDetails) return null;
    
    if (typeof rawDetails === 'object') return rawDetails;
    
    try {
      return JSON.parse(rawDetails);
    } catch {
      return { raw: rawDetails };
    }
  };

  const paymentDetails = parsePaymentDetails();
  
  const isRubPayment = paymentDetails && 
    (paymentDetails.method === 'SBP' || 
     paymentDetails.method === 'sbp' ||
     paymentDetails.currency === 'RUB' ||
     paymentDetails.qrUrl);

  // Format payment method name
  const getPaymentMethodName = (method) => {
    const names = {
      'RUB_SBP': 'СБП',
      'RUB_VTB': 'ВТБ',
      'RUB': 'Банковский перевод',
      'USDT': 'USDT'
    };
    return names[method] || method;
  };

  // Loading state
  if (isLoading && !liveOrderData) {
    return (
      <div className="min-h-screen bg-gray-950 p-4">
        <div className="max-w-sm mx-auto pt-20 text-center">
          <RefreshCw className="w-10 h-10 text-gray-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Загрузка данных заявки</p>
        </div>
      </div>
    );
  }

  // Error state
  if ((error && !liveOrderData) || isFailed) {
    return (
      <div className="min-h-screen bg-gray-950 p-4">
        <div className="max-w-sm mx-auto pt-20">
          <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-semibold text-white text-center mb-2">
              {isFailed ? 'Ошибка обработки заявки' : 'Не удалось загрузить заявку'}
            </h2>
            
            {/* Description */}
            <p className="text-gray-400 text-sm text-center mb-6">
              {isFailed 
                ? 'При обработке вашей заявки произошла ошибка. Пожалуйста, свяжитесь с поддержкой для решения проблемы.'
                : error || 'Произошла непредвиденная ошибка'
              }
            </p>

            {/* Order ID if available */}
            {displayData?.orderId && (
              <div className="bg-gray-800/50 rounded-xl p-3 mb-6 text-center">
                <span className="text-gray-500 text-xs">Номер заявки</span>
                <p className="text-white font-mono text-lg">{displayData.orderId}</p>
              </div>
            )}
            
            {/* Support Button */}
            <button
              onClick={openSupportChat}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-xl py-4 font-medium transition-all flex items-center justify-center gap-3 mb-3"
            >
              <Send className="w-5 h-5" />
              Написать в поддержку
            </button>

            {/* Back Button */}
            <button
              onClick={() => {
                setCurrentScreen('main');
                setOrderDetails(null);
              }}
              className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl py-3 font-medium transition-all"
            >
              На главную
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!displayData) return null;

  const isCompleted = displayData.status === 'completed';
  const isCancelled = displayData.status === 'cancelled';
  const isPaid = displayData.status === 'paid';
  const isBank = ['RUB', 'RUB_SBP', 'RUB_VTB'].includes(displayData.paymentMethod);

  // Completed state
  if (isCompleted) {
    return (
      <div className="min-h-screen bg-gray-950 p-4">
        <div className="max-w-sm mx-auto pt-20">
          <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
            </div>
            
            {/* Title */}
            <h2 className="text-2xl font-semibold text-white text-center mb-1">
              Заявка выполнена
            </h2>
            <p className="text-gray-500 text-center text-sm mb-6">
              № {displayData.orderId || displayData.order_id}
            </p>

            {/* Details */}
            <div className="bg-gray-800/50 rounded-xl p-4 mb-6 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Сумма</span>
                <span className="text-green-400 font-semibold">{displayData.amount} CNY</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Кошелёк</span>
                <span className="text-white">{displayData.type === 'alipay' ? 'Alipay' : 'WeChat'}</span>
              </div>
            </div>
            
            {/* Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setCurrentScreen('profile');
                  setOrderDetails(null);
                }}
                className="w-full bg-green-600 hover:bg-green-500 text-white rounded-xl py-4 font-medium transition-all"
              >
                Мои заявки
              </button>
              <button
                onClick={() => {
                  setCurrentScreen('main');
                  setOrderDetails(null);
                }}
                className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl py-3 font-medium transition-all"
              >
                Новая заявка
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Cancelled state
  if (isCancelled) {
    return (
      <div className="min-h-screen bg-gray-950 p-4">
        <div className="max-w-sm mx-auto pt-20">
          <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-semibold text-white text-center mb-1">
              Заявка отменена
            </h2>
            <p className="text-gray-500 text-center text-sm mb-6">
              № {displayData.orderId || displayData.order_id}
            </p>

            {/* Message */}
            <div className="bg-gray-800/50 rounded-xl p-4 mb-6">
              <p className="text-gray-400 text-sm text-center">
                Заявка была отменена. Если у вас есть вопросы, обратитесь в поддержку.
              </p>
            </div>
            
            {/* Buttons */}
            <div className="space-y-3">
              <button
                onClick={openSupportChat}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-xl py-4 font-medium transition-all flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                Написать в поддержку
              </button>
              <button
                onClick={() => {
                  setCurrentScreen('main');
                  setOrderDetails(null);
                }}
                className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl py-3 font-medium transition-all"
              >
                На главную
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active order state
  return (
    <div className="min-h-screen bg-gray-950 p-4">
      <div className="max-w-md mx-auto pt-20">
        {/* Header Card */}
        <div className="bg-gray-900 rounded-2xl p-5 border border-gray-800 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-gray-500 text-xs mb-1">Заявка</p>
              <p className="text-white font-mono text-lg">№ {displayData.orderId || displayData.order_id}</p>
            </div>
            <div className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${
              displayData.status === 'pending' 
                ? 'bg-yellow-500/10 text-yellow-400'
                : displayData.status === 'processing'
                ? 'bg-blue-500/10 text-blue-400'
                : displayData.status === 'paid'
                ? 'bg-purple-500/10 text-purple-400'
                : 'bg-gray-800 text-gray-400'
            }`}>
              {isPaid ? <Clock className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
              {displayData.status === 'pending' ? 'Ожидает оплаты' : 
               displayData.status === 'processing' ? 'Обработка' :
               displayData.status === 'paid' ? 'Ожидайте перевода' :
               displayData.status}
            </div>
          </div>

          {/* Order Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-800/50 rounded-xl p-3">
              <p className="text-gray-500 text-xs mb-1">Сумма</p>
              <p className="text-white font-semibold">{displayData.amount} CNY</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-3">
              <p className="text-gray-500 text-xs mb-1">Кошелёк</p>
              <p className="text-white font-semibold">{displayData.type === 'alipay' ? 'Alipay' : 'WeChat'}</p>
            </div>
          </div>
        </div>

        {/* Paid Status - Awaiting CNY Transfer */}
        {isPaid && (
          <div className="bg-purple-500/5 border border-purple-500/20 rounded-2xl p-5 mb-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-purple-400 animate-pulse" />
              </div>
              <div>
                <h3 className="text-purple-400 font-medium mb-1">Ожидайте перевода юаней</h3>
                <p className="text-gray-400 text-sm">
                  Ваша оплата получена. Юани будут переведены на ваш {displayData.type === 'alipay' ? 'Alipay' : 'WeChat'} в ближайшее время.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Section */}
        {!isPaid && paymentDetails && (
          <div className="bg-gray-900 rounded-2xl p-5 border border-gray-800 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-gray-400" />
              <h3 className="text-white font-medium">
                Оплата — {getPaymentMethodName(displayData.paymentMethod)}
              </h3>
            </div>

            {/* KYC Warning */}
            <div className="mb-4 flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-400/90">
                Оплата возможна только с реквизитов человека, на кого была пройдена верификация KYC
              </p>
            </div>

            {/* RUB Payment */}
            {isBank && isRubPayment && (
              <>
                {/* Amount */}
                <div className="bg-gray-800/50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-xs mb-1">К оплате</p>
                      <p className="text-2xl font-bold text-white">
                        {paymentDetails.amount?.toLocaleString()} ₽
                      </p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(String(paymentDetails.amount), 'amount')}
                      className="p-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      {copiedField === 'amount' ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <Copy className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Payment Button */}
                {paymentDetails.qrUrl && (
                  <a
                    href={paymentDetails.qrUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-xl py-4 font-medium transition-all flex items-center justify-center gap-2 mb-3"
                  >
                    Перейти к оплате
                    <ArrowRight className="w-5 h-5" />
                  </a>
                )}

                {/* Copy Link */}
                {paymentDetails.qrUrl && (
                  <button
                    onClick={() => copyToClipboard(paymentDetails.qrUrl, 'link')}
                    className="w-full bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl py-3 font-medium transition-all flex items-center justify-center gap-2"
                  >
                    {copiedField === 'link' ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        Скопировано
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Скопировать ссылку
                      </>
                    )}
                  </button>
                )}

                {/* Timer */}
                {paymentDetails.expiresAt && (
                  <div className="mt-4 flex items-center justify-center gap-2 text-yellow-500 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>Оплатите в течение 15 минут</span>
                  </div>
                )}
              </>
            )}

            {/* USDT Payment */}
            {!isBank && paymentDetails.method === 'CRYPTO' && (
              <div className="space-y-4">
                <div className="bg-gray-800/50 rounded-xl p-4">
                  <p className="text-gray-500 text-xs mb-2">Сеть</p>
                  <p className="text-white font-mono">{paymentDetails.network || 'TRC-20'}</p>
                </div>

                <div className="bg-gray-800/50 rounded-xl p-4">
                  <p className="text-gray-500 text-xs mb-2">Адрес кошелька</p>
                  <div className="flex items-center gap-2">
                    <p className="text-white font-mono text-xs break-all flex-1">{paymentDetails.address}</p>
                    <button
                      onClick={() => copyToClipboard(paymentDetails.address, 'address')}
                      className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                    >
                      {copiedField === 'address' ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="bg-gray-800/50 rounded-xl p-4">
                  <p className="text-gray-500 text-xs mb-2">Сумма</p>
                  <div className="flex items-center justify-between">
                    <p className="text-xl font-bold text-green-400">
                      {paymentDetails.amount?.toFixed(2)} USDT
                    </p>
                    <button
                      onClick={() => copyToClipboard(String(paymentDetails.amount?.toFixed(2)), 'usdt')}
                      className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      {copiedField === 'usdt' ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-3">
                  <p className="text-yellow-400 text-xs text-center">
                    Убедитесь, что выбрана сеть {paymentDetails.network || 'TRC-20'}
                  </p>
                </div>
              </div>
            )}

            {/* Bybit Payment */}
            {!isBank && paymentDetails.method === 'BYBIT' && (
              <div className="space-y-4">
                <div className="bg-gray-800/50 rounded-xl p-4">
                  <p className="text-gray-500 text-xs mb-2">UID получателя</p>
                  <div className="flex items-center justify-between">
                    <p className="text-xl font-mono text-white">{paymentDetails.uid}</p>
                    <button
                      onClick={() => copyToClipboard(paymentDetails.uid, 'uid')}
                      className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      {copiedField === 'uid' ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="bg-gray-800/50 rounded-xl p-4">
                  <p className="text-gray-500 text-xs mb-2">Сумма</p>
                  <p className="text-xl font-bold text-orange-400">
                    {paymentDetails.amount?.toFixed(2)} USDT
                  </p>
                </div>

                <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-3">
                  <p className="text-blue-400 text-xs text-center">
                    Bybit: Активы → Внутренний перевод → Введите UID
                  </p>
                </div>
              </div>
            )}

            {/* Waiting for payment details */}
            {isBank && !isRubPayment && (
              <div className="flex items-center gap-3 py-4">
                <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />
                <p className="text-gray-400 text-sm">Генерация реквизитов...</p>
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        {!isPaid && (
          <div className="bg-gray-900 rounded-2xl p-5 border border-gray-800 mb-4">
            <h3 className="text-white font-medium mb-4">Инструкция</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-gray-400 font-medium">1</div>
                <p className="text-gray-400 text-sm pt-0.5">Нажмите «Перейти к оплате» и завершите платёж</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-gray-400 font-medium">2</div>
                <p className="text-gray-400 text-sm pt-0.5">Дождитесь подтверждения оплаты</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0 text-xs text-gray-400 font-medium">3</div>
                <p className="text-gray-400 text-sm pt-0.5">Получите юани на {displayData.type === 'alipay' ? 'Alipay' : 'WeChat'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Connection Status */}
        <div className="flex items-center justify-center gap-2 mb-4 text-xs">
          {wsConnected ? (
            <>
              <Wifi className="w-3.5 h-3.5 text-green-500" />
              <span className="text-gray-500">Подключено</span>
            </>
          ) : (
            <>
              <WifiOff className="w-3.5 h-3.5 text-gray-600" />
              <span className="text-gray-600">Подключение...</span>
            </>
          )}
        </div>

        {/* Bottom Actions */}
        <div className="space-y-3">
          <button
            onClick={openSupportChat}
            className="w-full bg-gray-900 hover:bg-gray-800 text-gray-300 rounded-xl py-3.5 font-medium transition-all flex items-center justify-center gap-2 border border-gray-800"
          >
            <MessageCircle className="w-5 h-5" />
            Поддержка
          </button>
          <button
            onClick={() => {
              setCurrentScreen('main');
              setOrderDetails(null);
            }}
            className="w-full text-gray-500 hover:text-gray-400 py-3 text-sm transition-colors"
          >
            На главную
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationScreen;
